import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Service for extracting property values from JWT tokens
 * Allows retrieving specific properties from the ID token without repeating 
 * the token parsing logic throughout the application
 */
@Injectable({
  providedIn: 'root'
})
export class TokenPropertyService {
  // BehaviorSubject to track token state
  private tokenAvailable$ = new BehaviorSubject<boolean>(this.isTokenAvailable());
  
  constructor() {
    // Watch for localStorage changes to update the token state
    window.addEventListener('storage', () => {
      this.tokenAvailable$.next(this.isTokenAvailable());
    });
  }
  
  // Helper method to check if token is available
  private isTokenAvailable(): boolean {
    return !!localStorage.getItem('id_token');
  }
  
  // Method to update token state (to be called from TokenResolverService)
  public updateTokenState(): void {
    this.tokenAvailable$.next(this.isTokenAvailable());
  }
  
  // Public method to access token availability observable
  public getTokenAvailability(): Observable<boolean> {
    return this.tokenAvailable$.asObservable();
  }

  /**
   * Get a specific property from the ID token
   * @param propertyName The name of the property to extract from the token
   * @param tokenName Optional parameter to specify which token to use (defaults to 'id_token')
   * @returns The value of the property if found, or null if not found or token is invalid
   */
  getPropertyFromToken(propertyName: string, tokenName: string = 'id_token'): any {
    try {
      // Get token from local storage
      const token = localStorage.getItem(tokenName);
      
      if (!token) {
        console.warn(`No ${tokenName} found in localStorage`);
        return null;
      }
      
      // Parse the token payload (second part of JWT token) defensively
      const parts = token.split('.');
      if (!parts || parts.length < 2 || !parts[1]) {
        console.warn(`Invalid JWT format for ${tokenName}`);
        return null;
      }
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const tokenPayload = JSON.parse(atob(base64));
      
      // Return the requested property if it exists
      if (tokenPayload.hasOwnProperty(propertyName)) {
        return tokenPayload[propertyName];
      } else {
        console.warn(`Property '${propertyName}' not found in ${tokenName}`);
        return null;
      }
    } catch (error) {
      console.error(`Error extracting property '${propertyName}' from ${tokenName}:`, error);
      return null;
    }
  }

  /**
   * Get all properties from the ID token
   * @param tokenName Optional parameter to specify which token to use (defaults to 'id_token')
   * @returns All token payload properties as an object, or null if token is invalid
   */
  getAllProperties(tokenName: string = 'id_token'): any {
    try {
      // Get token from local storage
      const token = localStorage.getItem(tokenName);
      
      if (!token) {
        console.warn(`No ${tokenName} found in localStorage`);
        return null;
      }
      
      // Parse the token payload (second part of JWT token) defensively
      const parts = token.split('.');
      if (!parts || parts.length < 2 || !parts[1]) {
        console.warn(`Invalid JWT format for ${tokenName}`);
        return null;
      }
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const tokenPayload = JSON.parse(atob(base64));
      
      return tokenPayload;
    } catch (error) {
      console.error(`Error extracting properties from ${tokenName}:`, error);
      return null;
    }
  }

  /**
   * Check if a specific property exists in the ID token
   * @param propertyName The name of the property to check
   * @param tokenName Optional parameter to specify which token to use (defaults to 'id_token')
   * @returns Boolean indicating if the property exists
   */
  hasProperty(propertyName: string, tokenName: string = 'id_token'): boolean {
    try {
      const tokenPayload = this.getAllProperties(tokenName);
      return tokenPayload ? tokenPayload.hasOwnProperty(propertyName) : false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if a specific property in the ID token has a particular value
   * @param propertyName The name of the property to check
   * @param expectedValue The expected value of the property
   * @param tokenName Optional parameter to specify which token to use (defaults to 'id_token')
   * @returns Observable<boolean> indicating if the property has the expected value
   */
  propertyHasValue(propertyName: string, expectedValue: any, tokenName: string = 'id_token'): Observable<boolean> {
    return this.tokenAvailable$.pipe(
      map(() => {
        const propertyValue = this.getPropertyFromToken(propertyName, tokenName);
        return propertyValue === expectedValue;
      })
    );
  }

  /**
   * Checks if the user has Leaves access (HR or Supervisor persona)
   * @param tokenName Optional parameter to specify which token to use (defaults to 'id_token')
   * @returns Observable<boolean> indicating if the user has Leaves access
   */
  hasLeavesPersona(tokenName: string = 'id_token'): Observable<boolean> {
    return this.tokenAvailable$.pipe(
      map(() => {
        // Get personas array from token
        const personas = this.getPropertyFromToken('custom:persona', tokenName);
        
        // If personas is an array, check if it includes Leaves personas: "Supervisor" or "HR"
        if (Array.isArray(personas)) {
          return personas.some(p => p.toLowerCase() === 'supervisor' || p.toLowerCase() === 'hr');
        }
        
        // If personas doesn't exist or is not an array, return false
        return false;
      })
    );
  }
}
